// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import keys from "../utils/keys";
import { decryptToken } from "../utils/tokenSecure";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: keys.baseUrl,
  }),
  tagTypes: ["refetchSingleUser", "refetchPersonalThread"],
  endpoints: (builder) => ({
    getAllUsers: builder.query({
      query: (params) => ({
        url: `/user/admin/allusers${params}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    getUserProfile: builder.query({
      query: (params) => ({
        url: `/user/get-user-with-stripe${params}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: ["refetchSingleUser"],
    }),
    manageSubscription: builder.query({
      query: (params) => ({
        url: `/user/manage-subscription${params}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
      providesTags: ["refetchSingleUser"],
    }),
    paymentLinks: builder.query({
      query: () => ({
        url: `/payments/payment-links`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    updateTokenBalance: builder.mutation({
      query: (body) => ({
        url: "user/admin/update-token-balance",
        method: "PATCH",
        body: body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    updateAccountStatus: builder.mutation({
      query: (body) => ({
        url: "user/admin/update-account-status",
        method: "PATCH",
        body: body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    updateUserProfile: builder.mutation({
      query: (body) => ({
        url: "user/update-profile",
        method: "PATCH",
        body: body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      invalidatesTags: ["refetchSingleUser"],
      transformResponse: (response: any) => {
        return response;
      },
    }),
    updateUserPreferences: builder.mutation({
      query: (body) => ({
        url: "user/preferences" + body.params,
        method: "POST",
        body: body.body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    uploadProfilePicture: builder.mutation({
      query: (params) => ({
        url: "user/upload-profile-picture" + params.params,
        method: "PATCH",
        body: params.body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      invalidatesTags: ["refetchSingleUser"],
      transformResponse: (response: any) => {
        return response;
      },
    }),
    updatePersonalThread: builder.mutation({
      query: (params) => ({
        url: "user/personal-memory" + params.params,
        method: "PUT",
        body: params.body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      invalidatesTags: ["refetchPersonalThread"],
      transformResponse: (response: any) => {
        return response;
      },
    }),
    getPersonalThread: builder.query({
      query: (params) => ({
        url: `/user/personal-memory` + params,
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      providesTags: ["refetchPersonalThread"],
      transformResponse: (response: any) => {
        return response;
      },
    }),
    resetUserPassword: builder.mutation({
      query: (body) => ({
        url: "user/admin/reset-password",
        method: "POST",
        body: body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    createPersonalThread: builder.mutation({
      query: (body) => ({
        url: "/hiiboai/threads/personal",
        method: "POST",
        body: body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
    updatedPersonalMemory: builder.mutation({
      query: (body) => ({
        url: "/hiiboai/threads/personal/update",
        method: "PATCH",
        body: body,
        headers: {
          Authorization: `Bearer ${decryptToken()}`,
        },
      }),
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});
export const {
  useGetAllUsersQuery,
  useUpdateTokenBalanceMutation,
  useUpdateAccountStatusMutation,
  useResetUserPasswordMutation,
  useGetUserProfileQuery,
  useUploadProfilePictureMutation,
  useUpdateUserProfileMutation,
  useUpdateUserPreferencesMutation,
  useManageSubscriptionQuery,
  usePaymentLinksQuery,
  useUpdatePersonalThreadMutation,
  useGetPersonalThreadQuery,
  useCreatePersonalThreadMutation,
  useUpdatedPersonalMemoryMutation,
} = userApi;
